import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { findCategory, defaultCategory, menuCategories } from "../data/menu";
import CategoryStrip from "./CategoryStrip";
import MenuItemCard from "./MenuItemCard";

const dietOptions = [
  ["all", "All"],
  ["veg", "Veg"],
  ["nonveg", "Non-veg"],
];

// Full menu: category strip, veg filter and search, item grid.
export default function FullMenu({ categorySlug }) {
  const category = findCategory(categorySlug) || defaultCategory;
  const [diet, setDiet] = useState("all");
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return category.items.filter(
      (i) =>
        (diet === "all" || (diet === "veg" ? i.veg : !i.veg)) &&
        terms.every((t) => `${i.name} ${i.desc} ${i.withText || ""}`.toLowerCase().includes(t))
    );
  }, [category, diet, query]);

  const reset = () => {
    setDiet("all");
    setQuery("");
    const el = document.getElementById("ro-menu-search");
    if (el) el.focus();
  };

  const index = menuCategories.indexOf(category);

  return (
    <section className="ro-fullmenu" id="full-menu" aria-labelledby="ro-fullmenu-title" style={{ "--ro-theme": category.colour, "--ro-pale": category.pale }}>
      <div className="ro-container">
        <h2 id="ro-fullmenu-title" className="ro-fullmenu__title">
          The <span className="ro-outline">full</span> <strong className="ro-italic">menu</strong>
        </h2>
        <p className="ro-fullmenu__lead">
          {menuCategories.length} categories, every item from the current RollOver menu. Pick a category, then a dish.
        </p>
        <CategoryStrip activeSlug={category.slug} />
        <div id="ro-fullmenu-panel" role="tabpanel" aria-labelledby={`ro-cat-tab-${category.slug}`} tabIndex={-1} key={category.slug} className="ro-fullmenu__panel">
          <div className="ro-fullmenu__head">
            <div>
              <span className="ro-small">
                Category {index + 1} of {menuCategories.length}
              </span>
              <h3>
                <strong className="ro-italic">{category.name}</strong> <span className="ro-outline">{category.blurb}</span>
              </h3>
            </div>
            <div className="ro-filter ro-filter--menu">
              <div>
                <span className="ro-filter__label" id="ro-diet-label">
                  Show:
                </span>
                <div className="ro-filter__options" role="group" aria-labelledby="ro-diet-label">
                  {dietOptions.map(([v, l]) => (
                    <button key={v} type="button" aria-pressed={diet === v} onClick={() => setDiet(v)}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="ro-filter__label" htmlFor="ro-menu-search">
                  Search this category:
                </label>
                <input id="ro-menu-search" type="search" placeholder="Dish or ingredient" value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
              </div>
            </div>
          </div>
          <p className="ro-small ro-filter__count" role="status" aria-live="polite">
            {results.length} of {category.items.length} {category.name.toLowerCase()}
          </p>
          <div className="ro-item-grid">
            {results.length ? (
              results.map((i) => <MenuItemCard key={i.id} item={i} />)
            ) : (
              <div className="ro-no-results">
                <h3>Nothing matches here.</h3>
                <p>Try another word, or show everything in {category.name}.</p>
                <button type="button" className="ro-pill ro-pill--solid" onClick={reset}>
                  Show all {category.short.toLowerCase()}
                </button>
              </div>
            )}
          </div>
          <div className="ro-fullmenu__next">
            {index > 0 && (
              <Link className="ro-pill" to={`/menu/c/${menuCategories[index - 1].slug}`} state={{ preserveScroll: true }}>
                ← {menuCategories[index - 1].name}
              </Link>
            )}
            {index < menuCategories.length - 1 && (
              <Link className="ro-pill" to={`/menu/c/${menuCategories[index + 1].slug}`} state={{ preserveScroll: true }}>
                {menuCategories[index + 1].name} →
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
